/**
 * `F5` · `T-50`/`T-53` — Cómo una tablet cualquiera se convierte en «Caja 1».
 *
 * Tres pasos, y el tercero es el que importa:
 *
 *   1. El teléfono de la empleada → **nuestra API**, que encola el SMS.
 *   2. El código → **Supabase directo**, que lo verifica y devuelve un token.
 *   3. El token → `POST /mostrador/caja`, que crea la caja y, si todavía no
 *      tenía, guarda su PIN.
 *
 * `Q-18` se sostiene por esa separación: NestJS **no puede leer el código** ni
 * queriendo, porque nunca pasa por él.
 *
 * ── No hay código de alta, y fue una decisión ─────────────────────────────
 * Lo evidente era que el dueño registrara la tablet en el panel y dictara un
 * código. Se descartó el 2026-09-22 porque no es lo que `Flujo-de-mostrador`
 * describe —el dueño registra a la PERSONA— y porque **no compra seguridad**: un
 * código impreso en el panel viaja por WhatsApp igual que cualquier otro. Lo que
 * de verdad acota quién convierte un aparato en caja es el SMS a un teléfono ya
 * dado de alta como personal de esa cafetería.
 */
import { useState, type FormEvent, type ReactNode } from 'react'
import { normalizarONulo } from '@fialrest/shared'
import { verificarCodigo, mensajeParaLaPantalla } from '@fialrest/cliente'
import { elegirMiPin, montarCaja, pedirCodigo } from './api.js'
import type { TelefonoE164 } from '@fialrest/shared'

interface Props {
  readonly slug: string
  readonly alMontar: () => void
  /**
   * `T-70` · Qué viene a hacer quien está delante.
   *
   * `montar` es la tablet virgen: nadie la ha convertido en caja. `mi-pin` es la
   * segunda empleada de la cafetería, que llega a una caja ya montada y no tiene
   * PIN — hasta hoy su nombre salía en gris y no había nada que tocar.
   *
   * Los dos caminos prueban lo mismo —un SMS a un teléfono que ya es personal—
   * y por eso comparten pantalla. Lo que cambia es que el segundo **no crea un
   * aparato**: dejarla montar otra vez habría puesto una caja fantasma en la
   * lista de tablets del dueño.
   */
  readonly para?: 'montar' | 'mi-pin'
}

/**
 * En qué paso va, **y con qué teléfono**.
 *
 * Los dos datos son uno: el número probado sólo existe en el segundo paso, y no
 * existir antes no es un caso que atender. Estuvieron separados —`paso` y
 * `probado: TelefonoE164 | null`— y eso obligaba a un `=== null` en el envío que
 * no podía ocurrir nunca: una rama que ninguna prueba puede ver roja, que es
 * exactamente lo que el umbral de cobertura de esta app está para no dejar
 * pasar. Unirlos la borra en vez de fingir probarla.
 *
 * El normalizado se guarda al pasar de paso y no se vuelve a calcular: `R-14`,
 * una sola forma, decidida una sola vez. Y es `TelefonoE164` y no `string`
 * porque lo que se verifica tiene que ser el MISMO número con el que se pidió
 * el código — esta pantalla ya lo hacía bien; el panel no, y por eso nadie podía
 * entrar en él hasta el 2026-09-26.
 */
type Paso = { readonly en: 'telefono' } | { readonly en: 'codigo', readonly probado: TelefonoE164 }

export function Alta({ slug, alMontar, para = 'montar' }: Props): ReactNode {
  const [paso, setPaso] = useState<Paso>({ en: 'telefono' })
  const [telefono, setTelefono] = useState('')
  const [codigo, setCodigo] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const normalizado = normalizarONulo(telefono)

  const pedir = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (normalizado === null) { setError('Ese número no parece válido.'); return }
    setOcupado(true)
    setError(null)
    try {
      await pedirCodigo(normalizado)
      setPaso({ en: 'codigo', probado: normalizado })
    } catch (falla) {
      setError(mensajeParaLaPantalla(falla, 'No se pudo pedir el código.'))
    } finally {
      setOcupado(false)
    }
  }

  //  Recibe el teléfono en vez de leerlo del estado: el formulario que llama a
  //  esto sólo se pinta en el segundo paso, así que quien lo tiene es quien lo
  //  pasa. No hay ningún caso de «y si no hubiera».
  const montar = (probado: TelefonoE164) => async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setOcupado(true)
    setError(null)
    try {
      const token = await verificarCodigo(probado, codigo)
      if (para === 'mi-pin') {
        //  Sólo el PIN. Si el motor contesta que ya tenía uno, tampoco es un
        //  error: se vuelve a la lista y entra con el que tiene.
        await elegirMiPin({ slug, token, pin })
      } else {
        //  Sin nombre: lo pone el motor — 068. Ver la objeción del 2026-09-24.
        await montarCaja({ slug, token, ...(pin ? { pin } : {}) })
      }
      alMontar()
    } catch (falla) {
      //  `verificarCodigo` lanza un `Error` normal con su propio texto, y la API
      //  lanza `ErrorDeApi` con el suyo. Los dos están escritos para leerse.
      setError(mensajeParaLaPantalla(falla, 'No se pudo montar la caja.'))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <section className="caja-alta">
      <h1 className="caja-alta__titulo">
        {para === 'mi-pin'
          ? 'Vamos a comprobar que eres tú'
          : 'Esta tablet todavía no es una caja'}
      </h1>

      {paso.en === 'telefono' ? (
        <form className="caja-alta__forma" onSubmit={(e) => { void pedir(e) }}>
          <label className="caja-alta__etiqueta" htmlFor="telefono">
            Tu teléfono, el que tu encargada dio de alta
          </label>
          <input
            id="telefono" className="caja-alta__campo" type="tel" inputMode="tel"
            autoComplete="tel" value={telefono} autoFocus
            onChange={(e) => { setTelefono(e.target.value) }}
          />
          {error === null ? null : <p className="caja-alta__error" role="alert">{error}</p>}
          <button className="caja-alta__boton" type="submit" disabled={ocupado || normalizado === null}>
            {ocupado ? 'Enviando…' : 'Mándame un código'}
          </button>
        </form>
      ) : (
        <form className="caja-alta__forma" onSubmit={(e) => { void montar(paso.probado)(e) }}>
          <label className="caja-alta__etiqueta" htmlFor="codigo">El código del SMS</label>
          <input
            id="codigo" className="caja-alta__campo" type="text" inputMode="numeric"
            autoComplete="one-time-code" maxLength={6} value={codigo} autoFocus
            onChange={(e) => { setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6)) }}
          />

          {/*  > [!info] Aquí había un campo «cómo se llama esta caja», y se quitó
              El nombre lo pone el motor: `Caja <n> - <sucursal>`. Lo escribía la
              cajera y lo tiene que reconocer el DUEÑO en su lista —y teclearlo
              exacto para revocar—, así que no era suyo. Renombrar llega con
              `F6`, donde el nombre importa. Objeción de Isaac, 2026-09-24. */}

          <label className="caja-alta__etiqueta" htmlFor="pin-nuevo">
            Elige tu PIN, cuatro dígitos
          </label>
          {/*  Una vez en toda su vida laboral. Si ya lo tenía, esto se ignora en
              silencio: la policy de la 062 exige `pin_hash is null`, y decirle
              «no puedes cambiarlo» sería contestar a algo que no preguntó. */}
          <input
            id="pin-nuevo" className="caja-alta__campo" type="password" inputMode="numeric"
            autoComplete="off" maxLength={4} value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)) }}
          />

          {error === null ? null : <p className="caja-alta__error" role="alert">{error}</p>}
          <button
            className="caja-alta__boton" type="submit"
            //  Al elegir sólo el PIN es obligatorio: quien llega aquí viene a
            //  eso. Al montar es opcional —quien ya lo tiene está poniendo una
            //  caja más, no volviendo a nacer— y por eso las dos condiciones.
            disabled={ocupado || codigo.length !== 6 || (para === 'mi-pin' && pin.length !== 4)}
          >
            {ocupado
              ? (para === 'mi-pin' ? 'Guardando…' : 'Montando…')
              : (para === 'mi-pin' ? 'Guardar mi PIN' : 'Montar esta caja')}
          </button>
        </form>
      )}
    </section>
  )
}
